import { useId, useState, type InputHTMLAttributes } from 'react';
import { Icon } from '@/components/Icon';
import { useT } from '@/i18n/react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  hint?: string;
  error?: string;
}

/**
 * Szövegmező — `type="password"` esetén szem-ikonnal.
 *
 * A megjelenítő gomb ITT van, nem a hívó képernyőkön: minden jelszómező
 * ugyanazt kapja (belépés, regisztráció, és ami később jön), és nem kell
 * képernyőnként újra megírni. A gomb billentyűzettel is elérhető, de nem
 * `submit` — különben az Enter a mutatást indítaná a belépés helyett.
 */
export function TextField({ label, hint, error, id, type = 'text', ...rest }: TextFieldProps) {
  const t = useT();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);

  return (
    <div className={['field', error ? 'field--error' : ''].filter(Boolean).join(' ')}>
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      <div className="field__control">
        <input
          id={inputId}
          className={['field__input', isPassword ? 'field__input--reveal' : ''].filter(Boolean).join(' ')}
          type={isPassword && revealed ? 'text' : type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            className="field__reveal"
            onClick={() => setRevealed((current) => !current)}
            aria-label={revealed ? t.password.hide : t.password.show}
            aria-pressed={revealed}
            disabled={rest.disabled}
          >
            <Icon name={revealed ? 'eye-off' : 'eye'} size={20} />
          </button>
        ) : null}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="field__error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
